import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtNum, curSym, errMsg } from "@finance/lib/api";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@finance/components/ui/dialog";

const k = (it) => `${it.transaction_id || ""}:${it.line_index}`;

// Credit note from an invoice: lines pre-filled, qty editable down (never above invoiced).
export function CreditNoteDialog({ inv, note, onClose, onSaved }) {
  const [qty, setQty] = useState({});
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  useEffect(() => {
    const q = {};
    (inv?.items || []).forEach((it) => { q[k(it)] = it.qty; });
    if (note) { Object.keys(q).forEach((x) => { q[x] = 0; }); (note.items || []).forEach((it) => { q[k(it)] = it.qty; }); setReason(note.reason || ""); }
    setQty(q);
  }, [inv, note]);
  if (!inv) return null;
  const items = (inv.items || []).map((it) => ({ transaction_id: it.transaction_id || inv.transaction_id, line_index: it.line_index, qty: Number(qty[k(it)] || 0) })).filter((x) => x.qty > 0);
  const total = (inv.items || []).reduce((s, it) => s + Number(qty[k(it)] || 0) * (it.unit_price || 0), 0);
  const save = async () => {
    try {
      if (note) await api.put(`/credit-notes/${note.id}`, { items, reason, date });
      else await api.post("/credit-notes", { source_invoice_id: inv.id, items, reason, date });
      toast.success(note ? "Credit note updated — back to draft" : "Credit note saved as draft"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="cn-dialog">
        <DialogHeader><DialogTitle className="font-display">{note ? `Edit ${note.note_no}` : "Credit Note"} · invoice {inv.invoice_no}</DialogTitle></DialogHeader>
        <table className="w-full text-sm"><thead><tr className="lx-label text-gray-400"><th className="text-left">Item</th><th className="text-right">Invoiced</th><th className="text-right">Price</th><th className="text-right">Credit qty</th></tr></thead>
          <tbody>{(inv.items || []).map((it) => (
            <tr key={k(it)} className="border-t">
              <td className="py-1">{it.description}</td><td className="text-right lx-num">{fmtNum(it.qty)}</td><td className="text-right lx-num">{fmtNum(it.unit_price)}</td>
              <td className="py-1 text-right"><Input type="number" className="ml-auto h-8 w-24 text-right" data-testid={`cn-qty-${it.line_index}`} value={qty[k(it)] ?? ""}
                onChange={(e) => { let v = e.target.value; if (v !== "" && Number(v) > it.qty) v = String(it.qty); setQty((s) => ({ ...s, [k(it)]: v })); }} /></td>
            </tr>))}</tbody></table>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs">Reason *<Input className="mt-1" data-testid="cn-reason" value={reason} onChange={(e) => setReason(e.target.value)} /></label>
          <label className="text-xs">Date<Input type="date" className="mt-1" data-testid="cn-date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm">Excl. {curSym(inv.currency)} {fmtNum(total)} <span className="text-xs text-gray-400">(+ VAT at the invoice rate)</span></span>
          <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="cn-save" onClick={save}>Save draft</Button>
        </div>
        <p className="text-[11px] text-gray-400">Draft → Submit → Ronick approves → posts to the GL. Approved credit notes can only be reversed.</p>
      </DialogContent>
    </Dialog>
  );
}
